import {useCallback, useMemo, useRef, useState} from 'react';
import {
  Absence,
  AbsenceType,
  getHumanizedDiff,
  useAbsencesOperations,
  useSnacksActions,
} from '../../../../services';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import {t} from '../../../../localization';
dayjs.extend(minMax);

type AbsenceEditState = {
  memberId: number | undefined;
  absenceTypeId: number | undefined;
  fromDate: string | undefined;
  toDate: string | undefined;
  comment: string | undefined;
};

/**
 * Sets up the data context and handlers for adding absence for a person
 * @param teamId
 * @param callback
 */
export const useAbsenceModalState = (
  teamId: number,
  absence: Absence | undefined,
  absenceType: AbsenceType | undefined,
  callback: (success: boolean) => void,
) => {
  // Data holder
  const absenceStateRef = useRef<AbsenceEditState>({
    memberId: absence?.membershipId,
    absenceTypeId: absenceType?.id,
    comment: absence?.comment,
    fromDate: absence?.fromDate,
    toDate: absence?.toDate,
  });

  // Number of days between from / to
  const getNumberOfDays = useCallback(() => {
    if (absenceStateRef.current.fromDate && absenceStateRef.current.toDate) {
      return getHumanizedDiff(
        absenceStateRef.current.fromDate,
        dayjs(absenceStateRef.current.toDate).hour(23).minute(59),
      );
    }
    return '';
  }, []);
  const [numberOfDays, setNumberOfDays] = useState<string>(getNumberOfDays);

  // Validator
  const getIsValid = useCallback(() => {
    return (
      absenceStateRef.current.memberId !== undefined &&
      absenceStateRef.current.absenceTypeId !== undefined &&
      absenceStateRef.current.fromDate !== undefined &&
      absenceStateRef.current.toDate !== undefined
    );
  }, []);

  // State
  const [isValid, setIsValid] = useState<boolean>(getIsValid());

  // Handlers
  const handleToDateChanged = useCallback(
    (date: Date) => {
      let resolved = dayjs(date);
      if (absenceStateRef.current.toDate !== undefined) {
        resolved = dayjs.max(
          dayjs(resolved),
          dayjs(absenceStateRef.current.fromDate),
        );
      }
      resolved = resolved.hour(23).minute(59);
      absenceStateRef.current.toDate = resolved.toISOString();
      setIsValid(getIsValid());
      setNumberOfDays(getNumberOfDays());
    },
    [getIsValid, getNumberOfDays],
  );

  const handleFromDateChanged = useCallback(
    (date: Date) => {
      let resolved = dayjs(date);
      if (absenceStateRef.current.fromDate !== undefined) {
        resolved = dayjs.min(
          dayjs(resolved),
          dayjs(absenceStateRef.current.toDate),
        );
      }
      resolved = resolved.hour(0);
      resolved = resolved.minute(0);
      absenceStateRef.current.fromDate = resolved.toISOString();
      setIsValid(getIsValid());
      setNumberOfDays(getNumberOfDays());
    },
    [getIsValid, getNumberOfDays],
  );

  const handleMemberIdChanged = useCallback(
    (memberId: number | undefined) => {
      absenceStateRef.current.memberId = memberId;
      setIsValid(getIsValid());
    },
    [getIsValid],
  );

  const handleAbsenceTypeChanged = useCallback(
    (absenceTypeId: number | undefined) => {
      absenceStateRef.current.absenceTypeId = absenceTypeId;
      setIsValid(getIsValid());
    },
    [getIsValid],
  );

  const handleCommentChanged = useCallback(
    (t: string) => {
      absenceStateRef.current.comment = t;
      setIsValid(getIsValid());
    },
    [getIsValid],
  );

  // Submit
  const {
    create: createAbsence,
    update: updateAbsence,
  } = useAbsencesOperations();
  const {add: addSnack} = useSnacksActions();
  const submit = useCallback(() => {
    if (!isValid) {
      return;
    }

    let promise: ReturnType<typeof createAbsence | typeof updateAbsence>;
    if (absence) {
      // Update absence
      promise = updateAbsence(absence.id, {
        typeId: absenceStateRef.current.absenceTypeId,
        fromDate: dayjs(absenceStateRef.current.fromDate).format('YYYY-MM-DD'),
        toDate: dayjs(absenceStateRef.current.toDate).format('YYYY-MM-DD'),
        comment: absenceStateRef.current.comment,
      });
    } else {
      // Create absence
      promise = createAbsence({
        teamId,
        typeId: absenceStateRef.current.absenceTypeId,
        membershipId: absenceStateRef.current.memberId,
        fromDate: dayjs(absenceStateRef.current.fromDate).format('YYYY-MM-DD'),
        toDate: dayjs(absenceStateRef.current.toDate).format('YYYY-MM-DD'),
        comment: absenceStateRef.current.comment,
      });
    }

    absence
      ? addSnack(t('screens.schedule.snacks.absenceupdated'))
      : addSnack(t('screens.schedule.snacks.absencecreated'));
    callback(true);

    return promise;
  }, [
    isValid,
    absence,
    addSnack,
    callback,
    updateAbsence,
    createAbsence,
    teamId,
  ]);

  // Cancel
  const cancel = useCallback(() => callback(false), [callback]);

  return useMemo(() => {
    return {
      handleFromDateChanged,
      handleToDateChanged,
      handleMemberIdChanged,
      handleAbsenceTypeChanged,
      handleCommentChanged,
      isValid,
      numberOfDays,
      submit,
      cancel,
    };
  }, [
    handleAbsenceTypeChanged,
    handleCommentChanged,
    handleFromDateChanged,
    handleMemberIdChanged,
    handleToDateChanged,
    isValid,
    numberOfDays,
    submit,
    cancel,
  ]);
};
